import { Injectable } from "@angular/core";
import { HttpClientCustom } from "src/providers/custom/http-client";
import { TidyStorage } from "src/shared/providers/tidy-storage";
import { Communication } from 'src/providers/communication/communication';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Injectable({
  providedIn: 'root'
})

export class TeamService {

  constructor(
    private httpClient: HttpClientCustom,
    private storage: TidyStorage,
    private communication: Communication
  ) {}

  async getSMSForwardingText(page, forceRefresh = false) {
    const hasTeamsWithMembers = await this.getHasTeamsWithMembers(forceRefresh);
    const isRentalClient = localStorage.getItem('isRentalClient');
    return this.parseSMSForwardingText(page, hasTeamsWithMembers, isRentalClient);
  }

  async getHasTeamsWithMembers(forceRefresh): Promise<any> {
    let hasTeamsWithMembers = await this.storage.retrieve('hasTeamsWithMembers');
    const cacheIsExpired = this.checkIfCacheIsExpired(hasTeamsWithMembers);
    if (!hasTeamsWithMembers || cacheIsExpired || forceRefresh) {
      const teams = await this.getTeams();
      hasTeamsWithMembers = teams.some(team => team.team_members.length > 0);
      const cacheTime = new Date().getTime();
      hasTeamsWithMembers = {value: hasTeamsWithMembers, cacheTime};
      await this.storage.save('hasTeamsWithMembers', hasTeamsWithMembers);
    }
    return hasTeamsWithMembers.value;
  }

  parseSMSForwardingText(page, hasTeamsWithMembers, isRentalClient) {
    const t = (key: string) => new TranslationPipe().transform(key);
    if (hasTeamsWithMembers) {
      if (page === 'messages') {
        return {
          helper: 'SMS forwarding disabled',
          tooltip: t('SMS forwarding cannot work when you have multiple team members on your account. To get SMS notifications, edit Notification Settings.')
        }
      } else if (page === 'notification-settings') {
        return {
          helper: 'SMS forwarding disabled',
          tooltip: t('SMS forwarding cannot work when you have multiple team members on your account, but enable SMS notifications will send alerts when messages are received.')
        }
      }
    } else {
      if (page === 'messages') {
        const tooltipKey = isRentalClient === 'true'
          ? 'We forward chats from pros or guests to your SMS (in supported countries), and you can reply directly there.'
          : 'We forward chats from pros to your SMS (in supported countries), and you can reply directly there.';
        return {
          helper: 'SMS forwarding enabled',
          tooltip: t(tooltipKey)
        }
      } else if (page === 'notification-settings') {
        const tooltipKey = isRentalClient === 'true'
          ? 'We forward chats from pros or guests to your SMS (in supported countries), and you can reply directly there. SMS notifications may be unnecessary.'
          : 'We forward chats from pros to your SMS (in supported countries), and you can reply directly there. SMS notifications may be unnecessary.';
        return {
          helper: 'SMS forwarding enabled',
          tooltip: t(tooltipKey)
        }
      }
    }
  }

  checkIfCacheIsExpired(hasTeamsWithMembers) {
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - hasTeamsWithMembers?.cacheTime;
    return timeDiff > 1000 * 60 * 10; // 10 minutes in milliseconds
  }

  getTeams() {
    const url = 'api/v1/customer/account-teams?includes=team_members,addresses,account_team_members';
    return this.httpClient.get(url);
  }

  getTeamMembersForTeam(teamId) {
    const url = `api/v1/customer/account-team-members?account_team_id=${teamId}`;
    return this.httpClient.get(url);
  }

  createTeam(payload) {
    const url = 'api/v1/customer/account-teams';
    return this.httpClient.post(url, payload);
  }

  async addUserToTeam(payload) {
    const url = 'api/v1/customer/account-team-members';
    const response = await this.httpClient.post(url, payload);
    await this.storage.delete('hasTeamsWithMembers');
    return response;
  }

  addAddressesToTeam(payload) {
    const url = `api/v1/customer/account-team-addresses/add`;
    return this.httpClient.post(url, payload);
  }

  removeAddressesFromTeam(payload) {
    const url = `api/v1/customer/account-team-addresses/remove`;
    return this.httpClient.post(url, payload);
  }

  editTeamMember(teamMemberId, payload) {
    const url = `api/v1/customer/account-team-members/${teamMemberId}`;
    return this.httpClient.put(url, payload);
  }

  async removeUserFromTeam(teamMemberId) {
    const url = `api/v1/customer/account-team-members/${teamMemberId}`;
    const response = await this.httpClient.delete(url);
    await this.storage.delete('hasTeamsWithMembers');
    return response;
  }

  editTeam(teamId, payload) {
    const url = `api/v1/customer/account-teams/${teamId}`;
    return this.httpClient.put(url, payload);
  }

  async deleteTeam(teamId) {
    const url = `api/v1/customer/account-teams/${teamId}`;
    const response = await this.httpClient.delete(url);
    await this.storage.delete('hasTeamsWithMembers');
    return response;
  }

}
